// src/components/common/UpdateAvailableSnackbar.js
//
// Bottom-right notice shown once useVersionCheck finds that a newer build has been
// deployed than the one this tab is running. It never auto-hides — a notice that
// disappears while the user is looking elsewhere is one they never see. The close
// button hides it for now; it comes back the next time the tab regains focus, so
// nobody is nagged mid-task but nobody stays on a stale build for good either.

import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Snackbar } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useVersionCheck from '../../hooks/useVersionCheck';

function UpdateAvailableSnackbar() {
  const updateAvailable = useVersionCheck();
  const [dismissed, setDismissed] = useState(false);
  // A reload discards whatever a page holds only in its state (a draft view, a
  // typed message, unsaved settings). No page-wide registry of that exists, so
  // the notice asks every time rather than reloading under the user.
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!dismissed) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') setDismissed(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [dismissed]);

  return (
    <>
    <Snackbar
      open={updateAvailable && !dismissed}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      {/* MUI's Alert drops the close button `onClose` would render as soon as `action` is
          given, so the dismiss control has to be part of the action alongside Refresh. */}
      <Alert
        severity="info"
        variant="filled"
        action={(
          <>
            <Button color="inherit" size="small" onClick={() => setConfirmOpen(true)}>
              Refresh
            </Button>
            <IconButton
              aria-label="Dismiss"
              color="inherit"
              size="small"
              onClick={() => setDismissed(true)}
              sx={{ ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        )}
        sx={{ alignItems: 'center' }}
      >
        A new version of Decode is available. Refresh at your earliest convenience!
      </Alert>
    </Snackbar>
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Refresh Decode?</DialogTitle>
      <DialogContent>
        <DialogContentText>Any unsaved work will be lost. Do you wish to proceed?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={() => window.location.reload()}>Refresh</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export default UpdateAvailableSnackbar;
